import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { useSeo } from "../lib/seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";

export default function Scholarships() {
  const { user } = useAuth();
  const [elig, setElig] = useState(null);
  const [mine, setMine] = useState([]);
  useSeo("Student Scholarships");
  const load = () => {
    api.get("/scholarships/eligibility").then((r) => setElig(r.data)).catch(() => {});
    api.get("/scholarships/mine").then((r) => setMine(r.data.items)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  return (
    <div data-testid="scholarships-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
      <GraduationCap className="w-10 h-10 text-[#E65100]" />
      <div className="overline mt-4">॥ विद्या दानं महादानम् ॥</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Student Scholarships</h1>
      <p className="text-[#63534E] mt-5 text-base md:text-lg">The Seva Fund pays school and college fees for children of active <Link to="/seva-club" className="text-[#E65100]">Seva Club</Link> families. Students or parents can apply below with the fee details and a fee receipt or demand letter.</p>

      {!user && <div className="mt-10 bg-[#FFFDF9] sacred-border rounded-2xl p-6"><Link to="/login" className="text-[#E65100] font-semibold">Sign in</Link> with your Seva Club email to apply.</div>}
      {user && elig && !elig.eligible && (
        <div data-testid="scholarship-ineligible" className="mt-10 bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900">
          {elig.reason}. <Link to="/seva-club" className="font-semibold underline">Join the Seva Club</Link> to become eligible.
        </div>
      )}
      {user && elig?.eligible && <ScholarshipForm onSaved={load} />}

      {mine.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl text-[#4A0E17]">Your applications</h2>
          <div className="mt-4 space-y-3">
            {mine.map((s) => (
              <div key={s.id} data-testid={`my-scholarship-${s.id}`} className="bg-[#FFFDF9] sacred-border rounded-xl p-4 flex flex-wrap gap-3 items-center">
                <div className="flex-1"><div className="font-semibold text-[#4A0E17]">{s.student_name} · {s.course}</div><div className="text-xs text-[#63534E]">{s.institution} · {s.academic_year} · ${s.fees_needed_usd}</div></div>
                {s.amount_granted_usd > 0 && <span className="font-mono text-sm text-green-800">granted ${s.amount_granted_usd}</span>}
                <span className="text-xs capitalize bg-[#F3E5AB]/60 rounded-full px-2 py-0.5">{s.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ScholarshipForm({ onSaved }) {
  const blank = { student_name: "", relation: "parent", institution: "", course: "", academic_year: "2026-27", fees_needed_usd: "", reason: "" };
  const [f, setF] = useState(blank);
  const [feeDoc, setFeeDoc] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!feeDoc) return toast.error("Please attach the fee receipt or demand letter");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("fee_document", feeDoc);
    if (idDoc) fd.append("id_document", idDoc);
    setBusy(true);
    try { await api.post("/scholarships/apply", fd); toast.success("Application submitted"); setF(blank); setFeeDoc(null); e.target.reset(); onSaved(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="scholarship-form" className="mt-10 bg-[#FFFDF9] sacred-border rounded-3xl p-7 grid sm:grid-cols-2 gap-4">
      <Input data-testid="sc-student" required minLength={2} placeholder="Student's full name" value={f.student_name} onChange={set("student_name")} className="bg-white h-11" />
      <select data-testid="sc-relation" value={f.relation} onChange={set("relation")} className="h-11 rounded-md border border-[#D4AF37]/40 bg-white px-3 text-sm">
        <option value="parent">I am the parent</option><option value="guardian">I am the guardian</option><option value="self">I am the student</option>
      </select>
      <Input data-testid="sc-institution" required minLength={3} placeholder="School / college" value={f.institution} onChange={set("institution")} className="bg-white h-11" />
      <Input data-testid="sc-course" required placeholder="Class / course (e.g. Class 9, B.Sc)" value={f.course} onChange={set("course")} className="bg-white h-11" />
      <Input data-testid="sc-year" required placeholder="Academic year" value={f.academic_year} onChange={set("academic_year")} className="bg-white h-11" />
      <Input data-testid="sc-fees" required type="number" min="1" placeholder="Fees needed (USD)" value={f.fees_needed_usd} onChange={set("fees_needed_usd")} className="bg-white h-11" />
      <Textarea data-testid="sc-reason" required minLength={30} rows={4} placeholder="Tell us about the student and why support is needed (at least 30 characters)" value={f.reason} onChange={set("reason")} className="bg-white sm:col-span-2" />
      <label className="text-sm text-[#4A0E17]">Fee receipt / demand letter<input data-testid="sc-fee-doc" type="file" accept="image/*,application/pdf" onChange={(e) => setFeeDoc(e.target.files?.[0])} className="block mt-1 text-xs" /></label>
      <label className="text-sm text-[#4A0E17]">Student ID (optional)<input data-testid="sc-id-doc" type="file" accept="image/*,application/pdf" onChange={(e) => setIdDoc(e.target.files?.[0])} className="block mt-1 text-xs" /></label>
      <Button data-testid="sc-submit" disabled={busy} className="sm:col-span-2 bg-[#E65100] hover:bg-[#BF360C] rounded-full h-12">{busy ? "Submitting…" : "Apply for scholarship"}</Button>
    </form>
  );
}
